export const BuiltInRoles = {
  SUPER_ADMIN: "Super Admin",
  TENANT_ADMIN: "Tenant Admin",
  HR_MANAGER: "HR Manager",
  SITE_MANAGER: "Site Manager",
  HSE_OFFICER: "HSE Officer",
  SUPERVISOR: "Supervisor",
  OPS_LEAD: "Ops Lead",
  VIEWER: "Viewer",
  ISSUER: "Issuer",
  WORKER: "Worker",
} as const;

export type BuiltInRoleValue = typeof BuiltInRoles[keyof typeof BuiltInRoles];

export const BUILT_IN_ROLE_VALUES: readonly BuiltInRoleValue[] = [
  BuiltInRoles.SUPER_ADMIN,
  BuiltInRoles.TENANT_ADMIN,
  BuiltInRoles.HR_MANAGER,
  BuiltInRoles.SITE_MANAGER,
  BuiltInRoles.HSE_OFFICER,
  BuiltInRoles.SUPERVISOR,
  BuiltInRoles.OPS_LEAD,
  BuiltInRoles.VIEWER,
  BuiltInRoles.ISSUER,
  BuiltInRoles.WORKER,
] as const;

export const Scopes = {
  PLATFORM: "platform",
  TENANT: "tenant",
  PARTNER: "partner",
  USER: "user",
} as const;

export type ScopeValue = typeof Scopes[keyof typeof Scopes];

// Pack 9.H — Task #1019, draft #798 closure.
// Reconciles the two role vocabularies that historically coexisted in the
// platform (snake_case / lowercase from the API + DB seeds, vs. the human
// labels rendered in the UI). Every alias maps to ONE canonical BuiltInRole,
// so callers can resolve any input — typed by an admin, persisted by a
// migration, or read off a memberships row — to the same role identity.
//
// Aliases must NEVER fork: each LHS appears exactly once. Adding a new role
// type means adding to BuiltInRoles AND adding any deprecated name aliases.
export const ROLE_ALIASES: Record<string, BuiltInRoleValue> = {
  // Tenant Admin synonyms (the most common drift surface)
  "Admin": BuiltInRoles.TENANT_ADMIN,
  "Administrator": BuiltInRoles.TENANT_ADMIN,
  "Tenant Administrator": BuiltInRoles.TENANT_ADMIN,
  "tenant_admin": BuiltInRoles.TENANT_ADMIN,
  "tenant-admin": BuiltInRoles.TENANT_ADMIN,
  "admin": BuiltInRoles.TENANT_ADMIN,

  // Super Admin synonyms
  "super_admin": BuiltInRoles.SUPER_ADMIN,
  "super-admin": BuiltInRoles.SUPER_ADMIN,
  "SuperAdmin": BuiltInRoles.SUPER_ADMIN,
  "Platform Admin": BuiltInRoles.SUPER_ADMIN,

  // HR Manager synonyms
  "hr_manager": BuiltInRoles.HR_MANAGER,
  "hr-manager": BuiltInRoles.HR_MANAGER,
  "HR": BuiltInRoles.HR_MANAGER,
  "Human Resources": BuiltInRoles.HR_MANAGER,

  // Site Manager synonyms
  "site_manager": BuiltInRoles.SITE_MANAGER,
  "site-manager": BuiltInRoles.SITE_MANAGER,
  "Site Lead": BuiltInRoles.SITE_MANAGER,

  // HSE Officer synonyms
  "hse_officer": BuiltInRoles.HSE_OFFICER,
  "hse-officer": BuiltInRoles.HSE_OFFICER,
  "HSE": BuiltInRoles.HSE_OFFICER,
  "Safety Officer": BuiltInRoles.HSE_OFFICER,

  // Supervisor synonyms
  "supervisor": BuiltInRoles.SUPERVISOR,
  "Site Supervisor": BuiltInRoles.SUPERVISOR,

  // Ops Lead synonyms
  "ops_lead": BuiltInRoles.OPS_LEAD,
  "ops-lead": BuiltInRoles.OPS_LEAD,
  "Operations Lead": BuiltInRoles.OPS_LEAD,
  "Operations Manager": BuiltInRoles.OPS_LEAD,

  // Viewer synonyms
  "viewer": BuiltInRoles.VIEWER,
  "Read Only": BuiltInRoles.VIEWER,
  "ReadOnly": BuiltInRoles.VIEWER,
  "read_only": BuiltInRoles.VIEWER,

  // Issuer synonyms
  "issuer": BuiltInRoles.ISSUER,
  "Credential Issuer": BuiltInRoles.ISSUER,

  // Worker synonyms (the platform's largest persona; many drift sources)
  "worker": BuiltInRoles.WORKER,
  "Field Worker": BuiltInRoles.WORKER,
  "field_worker": BuiltInRoles.WORKER,
  "Operator": BuiltInRoles.WORKER,
  "operator": BuiltInRoles.WORKER,
  "Crew Member": BuiltInRoles.WORKER,
};

export function isSuperAdminRole(role: string | null | undefined): boolean {
  if (!role) return false;
  return resolveRoleAlias(role) === BuiltInRoles.SUPER_ADMIN;
}

export function isTenantAdminRole(role: string | null | undefined): boolean {
  if (!role) return false;
  return resolveRoleAlias(role) === BuiltInRoles.TENANT_ADMIN;
}

export function resolveRoleAlias(role: string | null | undefined): string {
  if (!role) return "";
  return ROLE_ALIASES[role] ?? role;
}

/**
 * Returns true when the supplied string maps (after alias resolution) to a
 * canonical BuiltInRole. Lets callers reject unknown role names early.
 */
export function isCanonicalRole(role: string | null | undefined): role is BuiltInRoleValue {
  if (!role) return false;
  const resolved = resolveRoleAlias(role);
  return (BUILT_IN_ROLE_VALUES as readonly string[]).includes(resolved);
}
